// @flow

import React from 'react';
import Carousel from 'app/common/react/components/common/Carousel/Carousel';
import cx from 'classnames';
import StoryPinImage from 'app/common/react/components/PinRep/components/StoryPinImage';
import type { BasicImage } from 'app/packages/pinterest-flow-types/image';
import type { CarouselPinData } from 'app/packages/pinterest-flow-types/pin';
import { Experiment } from 'app/common/react/components/experiments';
import { PRODUCT_PIN_HEIGHT_WIDTH_RATIO } from 'app/common/react/components/shopping/ProductPin/consts';
import { Image as GestaltImage, Box } from 'gestalt';

// In order to make room for action buttons that overlay the PinImage, and also
// to give the user enough room to actually click into the closeup, we need to
// apply a min-height to elephant and tiny pins.
const PIN_IMAGE_MIN_HEIGHT = 120;

type Props = {|
  animateGif?: boolean,
  cropProductPinWidth?: number,
  highResOnly?: boolean,
  lowResOnly?: boolean,
  onError?: () => void,
  onLoad?: () => void,
  pin: {
    carousel_data?: CarouselPinData,
    description?: string,
    dominant_color?: string,
    embed?: {
      height?: number,
      src?: string,
      width?: number,
    },
    id: string,
    images: {
      '236x': BasicImage,
      '474x': BasicImage,
      '736x': BasicImage,
      orig: BasicImage,
    },
    isPromoted?: boolean,
    story_pin_data_id?: string,
    title?: string,
  },
  saved?: boolean,
|};

export const PinImage = ({
  cropProductPinWidth,
  highResOnly,
  lowResOnly,
  onError,
  onLoad,
  pin,
  saved = false,
  animateGif,
}: Props) => {
  const { carousel_data, description = '', dominant_color: dominantColor, id, images } = pin;
  if (carousel_data) {
    return (
      <Carousel
        onError={onError}
        onLoad={onLoad}
        highResOnly={highResOnly}
        lowResOnly={lowResOnly}
        pinId={id}
      />
    );
  }

  let imageSrc, srcSet, imageHeight, imageWidth;
  if (images) {
    // If high resolution only is selected, providing defensive fallback for sizes not available.
    const rootImage = highResOnly
      ? images['736x'] || images.orig || images['474x'] || images['236x']
      : images['236x'];

    imageHeight = rootImage.height;
    imageWidth = rootImage.width;
    imageSrc = rootImage.url;

    // If resolution is explicit, only provide root image in srcSet.
    if (lowResOnly || highResOnly) {
      srcSet = `${rootImage.url} 1x`;
    } else {
      srcSet = [
        `${images['236x'].url} 1x`,
        images['474x'] && `${images['474x'].url} 2x`,
        images['736x'] && `${images['736x'].url} 3x`,
        images.orig && `${images.orig.url} 4x`,
      ]
        .filter(Boolean)
        .join(', ');
    }
  }

  if (animateGif) {
    const gifWidth = Math.max((pin.embed && pin.embed.width) || 0, 236);
    const gifHeight = Math.max((pin.embed && pin.embed.height) || 0, 236);
    const gifUrl = pin.embed ? pin.embed.src : '';

    if (gifUrl) {
      imageHeight = gifHeight;
      imageWidth = gifWidth;
      imageSrc = gifUrl;
      srcSet = '';
    }
  }

  return (
    <Box minHeight={PIN_IMAGE_MIN_HEIGHT} display="flex" alignItems="center">
      <Box flex="grow">
        <div className={cx({ savedGradient: saved })}>
          {!!pin.story_pin_data_id ? (
            <StoryPinImage pin={pin} onError={onError} onLoad={onLoad} />
          ) : (
            <Experiment name="web_closeup_related_products_entry">
              {({ group: closeupRelatedProductsGroup }) => (
                <Box
                  {...(cropProductPinWidth
                    ? {
                        height: cropProductPinWidth * PRODUCT_PIN_HEIGHT_WIDTH_RATIO,
                        width: cropProductPinWidth,
                      }
                    : {})}
                >
                  <Experiment name="web_image_loading_optimization">
                    {({ group: imageLoadingOptimizationGroup }) => (
                      <GestaltImage
                        alt={description}
                        color={dominantColor}
                        fit={cropProductPinWidth ? 'cover' : 'none'}
                        naturalHeight={imageHeight}
                        naturalWidth={imageWidth}
                        onError={onError}
                        onLoad={onLoad}
                        src={imageSrc}
                        srcSet={srcSet}
                        loading={imageLoadingOptimizationGroup.includes('lazy') ? 'lazy' : 'auto'}
                        importance={imageLoadingOptimizationGroup.includes('low') ? 'low' : 'auto'}
                      />
                    )}
                  </Experiment>
                </Box>
              )}
            </Experiment>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default PinImage;
